// Owner Money Flow — subscribe analytics, top creators, ledger drill-down. Live via WS.
import { useEffect, useRef, useState } from "react";
import { TrendingUp, AlertCircle, CheckCircle2, Coins, Filter, Download, Wifi } from "lucide-react";
import api from "../../lib/api";

const OUTCOME_LABELS = {
  success: "Success",
  failed_self_subscribe: "Self-sub blocked",
  failed_creator_not_found: "Bad creator id",
  failed_insufficient_funds: "Not enough NX",
  failed_during_writes: "Rolled back",
};

const OUTCOME_COLORS = {
  success: "text-nexus-mint border-nexus-mint/40 bg-nexus-mint/10",
  failed_self_subscribe: "text-nexus-amber border-nexus-amber/40 bg-nexus-amber/10",
  failed_creator_not_found: "text-nexus-amber border-nexus-amber/40 bg-nexus-amber/10",
  failed_insufficient_funds: "text-nexus-amber border-nexus-amber/40 bg-nexus-amber/10",
  failed_during_writes: "text-nexus-crimson border-nexus-crimson/40 bg-nexus-crimson/10",
};

export function MoneyFlowTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [logFilter, setLogFilter] = useState("");
  const [wsLive, setWsLive] = useState(false);
  const [livePings, setLivePings] = useState(0);
  const wsRef = useRef(null);

  const refresh = () => {
    api.get(`/owner/money-flow?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
    const url = `/owner/subscribe-log?days=${days}&limit=200${logFilter ? `&outcome=${logFilter}` : ""}`;
    api.get(url).then(({ data }) => setLog(data || [])).catch(() => {});
  };

  useEffect(() => { refresh(); }, [days, logFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live WS push — refresh both endpoints whenever a subscribe event fires.
  useEffect(() => {
    const token = localStorage.getItem("nexus_token");
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/ws/owner/money-flow?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setWsLive(true);
    ws.onclose = () => setWsLive(false);
    ws.onerror = () => setWsLive(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "subscribe.success") {
          setLivePings((n) => n + 1);
          refresh();
        }
      } catch (_e) { /* ignore */ }
    };
    return () => { try { ws.close(); } catch (_e) { /* ignore */ } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const token = localStorage.getItem("nexus_token");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/owner/money-flow/export.csv?days=${days}${logFilter ? `&outcome=${logFilter}` : ""}`;
    // Use fetch + Blob to attach Authorization header (a plain <a download> can't set headers)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `nexus-money-flow-${days}d.csv`;
        a.click();
      });
  };

  if (!data) return <div className="p-6 text-white/40 text-sm">Loading money flow…</div>;

  const maxSpark = Math.max(1, ...data.sparkline.map((s) => s.subs));

  return (
    <div className="space-y-6" data-testid="money-flow-tab">
      {/* Top bar — window + WS status + CSV export */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="label-eyebrow flex items-center gap-2"><Filter className="w-3 h-3" />// window</div>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-display border ${days === d ? "bg-nexus-cyan text-nexus-obsidian border-nexus-cyan" : "border-white/10 text-white/60 hover:text-white"}`}
            data-testid={`mf-window-${d}`}
          >
            {d}d
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-2 py-1 border text-[10px] tracking-[0.2em] uppercase font-display ${wsLive ? "border-nexus-mint/40 text-nexus-mint" : "border-white/10 text-white/40"}`} data-testid="mf-ws-status">
            <Wifi className={`w-3 h-3 ${wsLive ? "animate-pulse" : ""}`} />
            {wsLive ? `Live · ${livePings}` : "Offline"}
          </div>
          <button onClick={exportCsv} className="btn-ghost" data-testid="mf-export-csv"><Download className="w-3 h-3" />Export CSV</button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid sm:grid-cols-4 gap-3">
        <KPI label="Attempts" value={data.totals.attempts} testid="mf-kpi-attempts" />
        <KPI label="Success" value={data.totals.success} suffix={`${data.totals.success_rate}%`} accent="text-nexus-mint" testid="mf-kpi-success" />
        <KPI label="Creator inflow" value={`${data.creator_inflow_nx_total.toFixed(0)} NX`} accent="text-nexus-cyan" testid="mf-kpi-creator-in" />
        <KPI label="Owner inflow" value={`${data.owner_inflow_nx_total.toFixed(0)} NX`} accent="text-nexus-gold" testid="mf-kpi-owner-in" />
      </div>

      {/* Outcome breakdown */}
      <div className="grid sm:grid-cols-5 gap-2">
        {Object.entries(OUTCOME_LABELS).map(([k, label]) => (
          <div key={k} className={`p-3 border ${OUTCOME_COLORS[k]}`} data-testid={`mf-outcome-${k}`}>
            <div className="text-[9px] tracking-[0.25em] uppercase font-display opacity-80">{label}</div>
            <div className="font-display text-2xl mt-1">{data.by_outcome[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Sparkline */}
      {data.sparkline.length > 0 && (
        <div className="bg-nexus-surface/40 border border-white/5 p-4" data-testid="mf-sparkline">
          <div className="label-eyebrow flex items-center gap-2 mb-3"><TrendingUp className="w-3 h-3" />// daily successful subscribes</div>
          <div className="flex items-end gap-1 h-24">
            {data.sparkline.map((s) => (
              <div key={s.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div
                  className="w-full bg-nexus-cyan/70 group-hover:bg-nexus-cyan transition-colors relative"
                  style={{ height: `${(s.subs / maxSpark) * 100}%`, minHeight: "2px" }}
                  title={`${s.date}: ${s.subs}`}
                />
                <div className="text-[8px] text-white/30 font-mono">{s.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top creators / subscribers */}
      <div className="grid lg:grid-cols-2 gap-4">
        <TopList
          title="Top creators (by inflow)"
          accent="text-nexus-cyan"
          rows={data.top_creators}
          getMetric={(r) => `+${r.inflow_nx.toFixed(0)} NX · ${r.sub_count} subs`}
          testidPrefix="mf-top-creator"
        />
        <TopList
          title="Top subscribers (by outflow)"
          accent="text-nexus-gold"
          rows={data.top_subscribers}
          getMetric={(r) => `-${r.outflow_nx.toFixed(0)} NX · ${r.sub_count} subs`}
          testidPrefix="mf-top-subscriber"
        />
      </div>

      {/* Subscribe Log drilldown */}
      <div className="border-t border-white/10 pt-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-eyebrow flex items-center gap-2"><AlertCircle className="w-3 h-3" />// subscribe log · raw ledger</div>
          <div className="flex gap-1 border border-white/10 p-0.5">
            <button onClick={() => setLogFilter("")} className={`px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase font-display ${!logFilter ? "bg-white/10 text-white" : "text-white/50"}`} data-testid="mf-log-filter-all">All</button>
            {Object.entries(OUTCOME_LABELS).map(([k, label]) => (
              <button key={k} onClick={() => setLogFilter(k)} className={`px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase font-display ${logFilter === k ? "bg-white/10 text-white" : "text-white/50"}`} data-testid={`mf-log-filter-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="space-y-px bg-white/5 max-h-80 overflow-y-auto" data-testid="mf-log-list">
          {log.length === 0 && <div className="bg-nexus-surface p-4 text-center text-white/40 text-xs">No entries match this filter.</div>}
          {log.map((r) => (
            <div key={r.id} className="bg-nexus-surface px-3 py-2 flex items-center gap-3 text-[11px]" data-testid={`mf-log-row-${r.id}`}>
              {r.outcome === "success" ? <CheckCircle2 className="w-3 h-3 text-nexus-mint shrink-0" /> : <AlertCircle className="w-3 h-3 text-nexus-amber shrink-0" />}
              <div className="font-mono text-white/40 shrink-0">{(r.ts || "").slice(5, 16).replace("T", " ")}</div>
              <div className={`px-1.5 py-0.5 text-[8px] tracking-[0.2em] uppercase font-display border ${OUTCOME_COLORS[r.outcome] || "border-white/10"}`}>{OUTCOME_LABELS[r.outcome] || r.outcome}</div>
              {r.period && <div className="text-[9px] uppercase tracking-wider text-white/40">{r.period}</div>}
              <div className="flex-1 truncate text-white/60">
                sub <span className="font-mono text-white/40">{(r.subscriber_id || "").slice(0, 8)}</span> → creator <span className="font-mono text-white/40">{(r.creator_id || "").slice(0, 8)}</span>
                {r.price_nx && <span className="ml-2 text-nexus-cyan">{r.price_nx} NX</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, suffix, accent = "", testid }) {
  return (
    <div className="bg-nexus-surface p-4 border border-white/10" data-testid={testid}>
      <div className="label-eyebrow">// {label}</div>
      <div className={`font-display text-2xl mt-1 ${accent}`}>{value}</div>
      {suffix && <div className="text-[10px] text-white/40 mt-1">{suffix}</div>}
    </div>
  );
}

function TopList({ title, accent, rows, getMetric, testidPrefix }) {
  return (
    <div className="bg-nexus-surface/40 border border-white/5 p-4">
      <div className="label-eyebrow flex items-center gap-2 mb-3"><Coins className="w-3 h-3" />// {title}</div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-[11px] text-white/40">No activity in this window.</div>}
        {rows.map((r, i) => (
          <div key={r.creator_id || r.subscriber_id} className="flex items-center gap-3" data-testid={`${testidPrefix}-${i}`}>
            <span className="text-[10px] tracking-[0.2em] text-white/30 font-display w-4">#{i + 1}</span>
            <img src={r.avatar} alt="" className="w-7 h-7 ring-1 ring-white/10" />
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{r.display_name}</div>
              <div className={`text-[10px] ${accent}`}>{getMetric(r)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
